import * as yup from "yup";

import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Divider,
	Toolbar,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import {
	BackdropCircularProgress,
	EnhancedButtonPrimeReact,
	EnhancedButtonPrimeReactSeverity,
	EnhancedDropDownRHFWithLabel,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	LabelPosition,
	PhoneNumberRHF,
	RHookFormMaskedTextFieldWithLabel,
	RHookFormPasswordFieldWithLabel,
	RHookFormTextFieldWithLabel,
	ResetButton,
	SingleColumnLayout,
	Spacer,
	StandardPanel,
	StyledRouterLink,
	SubmitButton,
	SubmitButtonVariant,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { AuthService } from "../../services/AuthService";
import Grid from "@mui/material/Grid";
import { INITIAL_SNACKBAR_DATA } from "../../common/constants";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import { consoleValue } from "../../common/Common";
import { useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IUserAuthentication {
	choosePassword: string;
	confirmPassword: string;
	// contact: string;
	// countryCode?: string;
	verify_key?: string;
	email?: string;
	accessToken?: string;
}

const useGetUserDetail = (key: string | undefined) =>
	useQuery({
		queryKey: ["authentication-data"],
		queryFn: (data) => {
			return AuthService.authenticationData(key);
		},
		refetchOnWindowFocus: false,
	});

const useGetVerfyEmailKey = (key: string | undefined) =>
	useQuery({
		queryKey: ["verify-email"],
		queryFn: (data) => {
			return AuthService.verifyEmail(key);
		},
		refetchOnWindowFocus: false,
	});

const userAuthenticationFormSchema = yup.object({
	choosePassword: yup
		.string()
		.required("Please Enter New Password")
		.min(8, "Your password must be at least 8 characters long")
		.max(15, "Your password Should be maximum 15 characters")
		.matches(
			/^(?=.*[A-Za-z])(?=.*\d).+$/,
			"Password must contain at least 1 letter and 1 number"
		),
	confirmPassword: yup
		.string()
		.required("Please Confirm new password")
		.min(8, "Your password must be at least 8 characters long")
		.max(15, "Your password Should be maximum 15 characters")
		.matches(
			/^(?=.*[A-Za-z])(?=.*\d).+$/,
			"Password must contain at least 1 letter and 1 number"
		)
		.oneOf([yup.ref("choosePassword")], "Passwords don't match."),
	// contact: yup
	//   .string()
	//   .required("Phone is required")
	//   .min(14, "Should be maximum 15 characters"),
});
const AlreadyHaveAnAccountLinkComponent = () => {
	return (
		<Grid
			size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
			style={{ position: "relative", top: "1.3rem" }}
		>
			<Typography variant="subtitle2">
				Already have an account?&nbsp;
				<StyledRouterLink to="/login">Sign In</StyledRouterLink>
			</Typography>
		</Grid>
	);
};

const DoNotHaveAnAccountLinkComponent = () => {
	return (
		<Grid
			size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
			style={{ position: "relative", top: "1.3rem" }}
		>
			<Typography variant="subtitle2">
				Do not have an account yet?&nbsp;
				<StyledRouterLink to="/create-an-account">
					Create an Account
				</StyledRouterLink>
			</Typography>
		</Grid>
	);
};

const InvalidEmail = () => {
	return (
		<StandardPanel title={`Welcome to SimplePay`} testId={`welcome-panel`}>
			<Alert severity="error" sx={{ marginBottom: "10px" }}>
				Invalid Email
			</Alert>
			<Box sx={{ textAlign: "right" }}>
				<AlreadyHaveAnAccountLinkComponent />
				<DoNotHaveAnAccountLinkComponent />
				<Spacer y={1.5} x={1.5} />
			</Box>
		</StandardPanel>
	);
};
const AccountCreatedSuccessfully = () => {
	return (
		<StandardPanel title={`Welcome to SimplePay`} testId={`welcome-panel`}>
			<Alert severity="success" sx={{ marginBottom: "1rem" }}>
				<AlertTitle>Registration Successful!</AlertTitle>
				<br />
				Please use your login details to access your account.
			</Alert>
			<Divider />
			<Box sx={{ textAlign: "center", marginTop: "1rem" }}>
				<StyledRouterLink to="/login">
					<Button variant="contained">Login</Button>
				</StyledRouterLink>
			</Box>
		</StandardPanel>
	);
};

const SecurityQuestionsScreenValidationSchema = yup.object().shape({
	questionOne: yup.string().required("Question is required"),

	questionTwo: yup
		.string()
		.required("Question is required")
		.test(
			"unique-question-two",
			"Please choose a different question",
			function (value) {
				const { questionOne } = this.parent;
				return value !== questionOne;
			},
		),

	questionThree: yup
		.string()
		.required("Question is required")
		.test(
			"unique-question-three",
			"Please choose a different question",
			function (value) {
				const { questionOne, questionTwo } = this.parent;
				return value !== questionOne && value !== questionTwo;
			},
		),

	answerOne: yup.string().required("Answer is required"),
	answerTwo: yup.string().required("Answer is required"),
	answerThree: yup.string().required("Answer is required"),
});
interface ISecurityQuestionsLayout {
	saveSecurityQuestionsFieldsData: any
	accessToken: any
}
const SecurityQuestionsLayout: React.FC<ISecurityQuestionsLayout> = ({ saveSecurityQuestionsFieldsData, accessToken }) => {
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);


	const DEFAULT_VALUES = {
		questionOne: undefined,
		questionTwo: undefined,
		questionThree: undefined,
		answerOne: undefined,
		answerTwo: undefined,
		answerThree: undefined,
	};

	const formMethods = useForm<any>({
		resolver: yupResolver(SecurityQuestionsScreenValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});

	const onSubmit = (values: any) => {
		let payload = {
			accessToken: accessToken,
			questionOne: values.questionOne,
			questionTwo: values.questionTwo,
			questionThree: values.questionThree,
			answerOne: values.answerOne,
			answerTwo: values.answerTwo,
			answerThree: values.answerThree,
		};
		saveSecurityQuestionsFieldsData(payload);
	};

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const securityQuestions = [
		{ label: "What is your Mother's birthplace?", value: "What is your Mother's birthplace?" },
		{ label: "What is the name of your first pet?", value: "What is the name of your first pet?" },
		{ label: "What is the name of your favorite teacher?", value: "What is the name of your favorite teacher?" },
		{ label: "What is the first name of your favorite uncle?", value: "What is the first name of your favorite uncle?" },
		{ label: "What is the first name of your favorite aunt?", value: "What is the first name of your favorite aunt?" },
		{ label: "What is your oldest cousin's name?", value: "What is your oldest cousin's name?" },
		{ label: "Where did you spend your honeymoon?", value: "Where did you spend your honeymoon?" },
		{ label: "What is your oldest child's nickname?", value: "What is your oldest child's nickname?" },
		{ label: "What is your youngest child's nickname?", value: "What is your youngest child's nickname?" },
	];

	return (
		<Grid>
			{/* <BackdropCircularProgress
				open={isSubmitting}
				testId="yooo"
			/> */}
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ md: 1, lg: 1, xl: 1 }}></Grid>
						<Grid size={{ xs: 10, sm: 10, md: 10, lg: 10, xl: 10 }}>
							<div
								style={{
									margin: "15px 0",
									fontSize: "1.5rem",
									fontWeight: "bold",
									fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
								}}
							>
								{"Answer the Questions"}
							</div>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="Answer Any 3 Questions"
										testId={`answer-any-three-question`}
										variant="outlined"
									// topPadding={2}
									>
										<Box>
											<EnhancedDropDownRHFWithLabel
												label="Question 1"
												items={securityQuestions}
												testId={"question-1"}
												selectName={"questionOne"}
											/>
											<RHookFormTextFieldWithLabel
												label="Answer"
												id="answerOne"
												testId={`answer-1`}
											/>
											<EnhancedDropDownRHFWithLabel
												label="Question 2"
												items={securityQuestions}
												testId={"question-2"}
												selectName={"questionTwo"}
											/>
											<RHookFormTextFieldWithLabel
												label="Answer"
												id="answerTwo"
												testId={`answer-2`}
											/>
											<EnhancedDropDownRHFWithLabel
												label="Question 3"
												items={securityQuestions}
												testId={"question-3"}
												selectName={"questionThree"}
											/>
											<RHookFormTextFieldWithLabel
												label="Answer"
												id="answerThree"
												testId={`answer-3`}
											/>
										</Box>
									</StandardPanel>
								}
								toolbar={
									// <SecurityQuestionsScreenToolbar
									// 	isSubmitting={false}
									// />
									// <></>
									<Toolbar>
										<>
											<EnhancedButtonPrimeReact
												label="Save"
												// loading={isSubmitting}
												type="submit"
												raised
												severity={EnhancedButtonPrimeReactSeverity.Success}
											/>
										</>
									</Toolbar>
								}
								showFullDiaplay={true}
								toolbarPosition="bottom"
								testId="form-layout"
							/>
						</Grid>
						<Grid size={{ md: 1, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"security-questions-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};

const UserAuthenticationFormComponent = () => {
	// RESPONSIVE
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
	const isMediumScreen01 = useMediaQuery(theme.breakpoints.between(900, 1200));
	const mobileTabOrSmallLaptopScreen = isMobile || isSmallScreen || isMediumScreen01;
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [successResponse, setSuccessResponse] = useState<any>(undefined);
	const [showSecurityQueestionsLayout, setShowSecurityQueestionsLayout] = useState<boolean>(false);
	const [verifyEmailResponse, setverifyEmailResponse] =
		useState<any>(undefined);
	const methods = useForm<any>({
		resolver: yupResolver(userAuthenticationFormSchema),
	});

	const { key } = useParams();


	//get data
	const { isPending: isPendingData, data: authenticationData } =
		useGetUserDetail(key);

	let emailKey = authenticationData && authenticationData.email;
	// const { isPending: isPendingVerifyEmail, data: verifyEmail } =
	//   useGetVerfyEmailKey(emailKey);


	const { isPending: isSubmitting, mutate: userAuthentication } = useMutation({
		mutationFn: AuthService.authentication,

		onSuccess: (res: IServerError | any | IResponse) => {
			if (res.message === "Password has been updated.") {
				setSuccessResponse(
					"Your registration is now complete. Your account activation link has been sent to your email address."
				);
				// setShowSecurityQueestionsLayout(true)
			} else {

				setSnackbarData({
					open: true,
					message: res?.message ? res.message : "Somethingwent wrong!",
					severity: res.status === false ? "error" : "success",
				});
			}
			// setShowSecurityQueestionsLayout(true)
		},
		onError: (err) => {
			// setShowSecurityQueestionsLayout(true)
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});
	const { isPending: isSubmittingSecurityQuestions, mutate: saveSecurityQuestionsFieldsData } =
		useMutation({
			mutationFn: AuthService.saveSecurityQuestions,
			onSuccess: (res: IServerError | IResponse | any) => {
				if (res.status === 200 || res.status === 201) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					setSuccessResponse(
						"Your registration is now complete. Your account activation link has been sent to your email address."
					);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
				// setSuccessResponse(
				// 	"Your registration is now complete. Your account activation link has been sent to your email address."
				// );
			},
			onError: () => {
				// setSuccessResponse(
				// 	"Your registration is now complete. Your account activation link has been sent to your email address."
				// );
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};
	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		methods.reset();
	};

	const onSubmit = (data: IUserAuthentication) => {
		let payload = {
			newPassword: data.choosePassword,
			confirmNewPassword: data.confirmPassword,
			// email: key && key,
			email: authenticationData?.email,
			accessToken: authenticationData?.accessToken,
			// contact: data.contact,
			// countryCode: data.countryCode,
			// verify_key: key,
		};
		userAuthentication(payload);

	};
	consoleValue(methods.formState.isDirty); // make sure formState is read before render to enable the Proxy

	useEffect(() => {
		(async () => {
			try {
				const response: any = await AuthService.verifyEmail(emailKey);

				if (response.data) {
					setverifyEmailResponse(response);
				}
			} catch (error) {
				console.error("Error fetching data:", error);
			}
		})();
	}, [authenticationData, emailKey]);

	console.log("authenticationData", authenticationData)
	return (
		<>
			{successResponse ? (
				<AccountCreatedSuccessfully />
			) : showSecurityQueestionsLayout ? (
				<SecurityQuestionsLayout
					accessToken={authenticationData?.accessToken}
					saveSecurityQuestionsFieldsData={saveSecurityQuestionsFieldsData}
				/>
			) : (
				<>
					<BackdropCircularProgress
						open={isPendingData || isSubmitting || isSubmittingSecurityQuestions}
						testId={"user-authentication"}
					/>
					{verifyEmailResponse && verifyEmailResponse.status === 200 ? (
						<FormProvider {...methods}>
							<form onSubmit={methods.handleSubmit(onSubmit)}>
								<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
									<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
										<StandardPanel
											title={
												`Welcome to SimplePay ${authenticationData && authenticationData.name
												}`}
											testId={`welcome-panel`}
										>
											<Alert severity="info">
												You are just a few steps away from creating an account
												in Simple Pay.
												<br />
												Please set your password below to continue.
											</Alert>
										</StandardPanel>
									</Grid>
									<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
										<StandardPanel
											title={`User Authentication`}
											testId={`user-authentication`}
											topPadding={3}
										>
											<RHookFormPasswordFieldWithLabel
												id="choosePassword"
												testId={`user-authentication-new-password`}
												label="Choose password"
												placeholder="Choose password"
												labelPosition={mobileTabOrSmallLaptopScreen ? LabelPosition.Top : LabelPosition.Left}
												required
											/>
											<RHookFormPasswordFieldWithLabel
												id="confirmPassword"
												testId={`user-authentication-confirm-new-password`}
												label="Confirm password"
												placeholder="Confirm password"
												labelPosition={mobileTabOrSmallLaptopScreen ? LabelPosition.Top : LabelPosition.Left}
												required
											/>
											{/* for save contact no and countrycode we have to use api from RBSC 
                  API to update phone 
                  /update_phone_and_send_code 
                  required_list = list(["email", "phone", "country_code", "access_token"]) */}
											{/* <Grid
                  container
                  columnSpacing={0}
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                >
                  <Grid item xs={8} sm={8} md={8} lg={8} xl={8}>
                    <PhoneNumberRHF
                      label="Contact No."
                      id="contact"
                      testId={`contact-number`}
                      labelPosition={LabelPosition.Left}
                      labelColumns={6}
                      fieldColumns={6}
                      required
                    />
                  </Grid>
                  <Grid item xs={1} sm={1} md={1} lg={1} xl={1}></Grid>
                  <Grid item xs={3} sm={3} md={3} lg={3} xl={3}>
                    <RHookFormMaskedTextFieldWithLabel
                      label="Country Code"
                      id="countryCode"
                      testId={`countryCode`}
                      labelPosition={LabelPosition.Left}
                      labelColumns={6}
                      fieldColumns={6}
                      placeholder="+1"
                      inputMaskPattern="+00"
                    />
                  </Grid>
                </Grid> */}
											<Grid
												container
												columnSpacing={0}
												size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
											>
												<Grid size={{ md: 4, lg: 4, xl: 4 }}></Grid>
												<Grid size={{ md: 8, lg: 8, xl: 8 }}>
													<SubmitButton
														label="Submit"
														variant={SubmitButtonVariant.Primary}
														loading={isSubmitting}
														testId="create-an-account-button"
														icon={<PlaylistAddCheckOutlinedIcon />}
													/>
													<ResetButton
														testId="reset-button"
														onClickHandler={onResetClicked}
													/>
												</Grid>
											</Grid>
										</StandardPanel>
									</Grid>
								</Grid>
							</form>
						</FormProvider>
					) : verifyEmailResponse ? (
						<StandardPanel
							title={`Welcome to SimplePay`}
							testId={`welcome-panel`}
						>
							<Alert severity="error" sx={{ marginBottom: "1rem" }}>
								<AlertTitle>Error</AlertTitle>
								<br />
								{verifyEmailResponse && verifyEmailResponse.message
									? verifyEmailResponse.message
									: "E-mail Does Not Exist!"}
							</Alert>
							<Divider />
							<Box sx={{ textAlign: "right" }}>
								<AlreadyHaveAnAccountLinkComponent />
								<DoNotHaveAnAccountLinkComponent />
							</Box>
						</StandardPanel>
					) : authenticationData &&
						authenticationData?.message && authenticationData?.message === "Invalid email key" ||
						authenticationData?.message && authenticationData?.message?.message === "Invalid email key." ? (
						<InvalidEmail />
					) : (
						authenticationData && (
							<>
								{/* <Alert severity="error">
								Somthing went wrong!
							</Alert> */}
							</>
						)
					)}
				</>
			)}
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"register-snackbar"}
				open={snackbarData.open}
			/>
		</>
	);
};

export const UserAuthentication = () => {
	return (
		<>
			<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<Grid size={{ md: 1, lg: 1, xl: 1 }}></Grid>
				<Grid size={{ md: 10, lg: 10, xl: 10 }}>
					<UserAuthenticationFormComponent />
				</Grid>
				<Grid size={{ md: 1, lg: 1, xl: 1 }}></Grid>
			</Grid>
		</>
	);
};
